import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import "../styles.css";

const TYPING_INPUT_TYPES = new Set([
  "insertText",
  "deleteContentBackward",
  "deleteContentForward",
  "insertCompositionText",
  "insertFromPaste",
  "insertFromDrop",
  "deleteByCut",
]);

const shouldDeferInputUpdate = (inputType) =>
  inputType && TYPING_INPUT_TYPES.has(inputType);

const clampNumericValue = (value, min, max) => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }
  let num = parseInt(String(value), 10);
  if (Number.isNaN(num)) {
    return null;
  }
  if (typeof min === "number" && num < min) {
    num = min;
  }
  if (typeof max === "number" && num > max) {
    num = max;
  }
  return num;
};

// Helper function to convert negative x/y values to 0 for display only
const getDisplayValue = (value) => {
  if (value === "" || value === null || value === undefined) return value;
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return num < 0 ? "0" : value;
};

// Utility to calculate points for a line based on start, length, angle
function calculateLinePoints(x, y, length, angleInDegrees) {
  const angleInRadians = (angleInDegrees * Math.PI) / 180;
  const endX = x + length * Math.cos(angleInRadians);
  const endY = y + length * Math.sin(angleInRadians);
  return [x, y, endX, endY];
}

function scalePoints(points, newWidth, newHeight) {
  // Find current bounding box
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (let i = 0; i < points.length; i += 2) {
    minX = Math.min(minX, points[i]);
    maxX = Math.max(maxX, points[i]);
    minY = Math.min(minY, points[i + 1]);
    maxY = Math.max(maxY, points[i + 1]);
  }
  const currentWidth = maxX - minX;
  const currentHeight = maxY - minY;

  // Handle edge cases for horizontal/vertical lines
  const scaleX = currentWidth > 0 ? newWidth / currentWidth : 1;
  const scaleY = currentHeight > 0 ? newHeight / currentHeight : 1;

  // Scale points
  const scaledPoints = points.map((val, idx) =>
    idx % 2 === 0 ? minX + (val - minX) * scaleX : minY + (val - minY) * scaleY
  );
  return scaledPoints;
}

function LineEditor({
  selectedLine,
  updateLineProperties,
  onDelete,
  canvasHeight,
}) {
  const EDITOR_EDGE_BUFFER_PX = 10;
  const MIN_X = EDITOR_EDGE_BUFFER_PX;
  const MIN_Y = EDITOR_EDGE_BUFFER_PX;

  const MIN_LENGTH = 1;
  const MAX_LENGTH = 1000; // This could be dynamically calculated too if needed

  const MIN_ANGLE = -360;
  const MAX_ANGLE = 360;

  const MIN_STROKE_WIDTH = 1;
  const MAX_STROKE_WIDTH = 20;

  const MIN_DOT_RADIUS = 0;
  const MAX_DOT_RADIUS = 20;

  // Add min/max constants for width and height
  const MIN_WIDTH = 30;
  const MAX_WIDTH = 570;
  const MIN_HEIGHT = 0;
  const MAX_HEIGHT = 600;

  // Dynamic canvas dimensions
  const canvasPixelHeight = (parseInt(canvasHeight) / 100) * window.innerHeight;

  const MAX_X = Math.max(MIN_X, window.innerWidth - EDITOR_EDGE_BUFFER_PX);
  const MAX_Y = Math.max(MIN_Y, canvasPixelHeight - EDITOR_EDGE_BUFFER_PX);

  const x = Number.isFinite(selectedLine.x || selectedLine.newX)
    ? Math.round(selectedLine.x || selectedLine.newX)
    : 0;
  const y = Number.isFinite(selectedLine.y || selectedLine.newY)
    ? Math.round(selectedLine.y || selectedLine.newY)
    : 0;
  const length = Number.isFinite(selectedLine.length)
    ? Math.round(selectedLine.length)
    : 100;
  const angle = Number.isFinite(selectedLine.angle)
    ? Math.round(selectedLine.angle)
    : 0;
  const strokeColor =
    selectedLine.stroke || selectedLine.strokeColor || "#000000";
  const strokeWidth = selectedLine.strokeWidth || 2;
  const isDashed = selectedLine.isDashed || false;

  const serializableStartDot = selectedLine.startDot
    ? {
        radius: selectedLine.startDot.radius,
        fill: selectedLine.startDot.fill,
        stroke: selectedLine.startDot.stroke,
        strokeWidth: selectedLine.startDot.strokeWidth,
      }
    : undefined;

  const serializableEndDot = selectedLine.endDot
    ? {
        radius: selectedLine.endDot.radius,
        fill: selectedLine.endDot.fill,
        stroke: selectedLine.endDot.stroke,
        strokeWidth: selectedLine.endDot.strokeWidth,
      }
    : undefined;

  const [linePresets, setLinePresets] = useState(() => {
    const saved = localStorage.getItem("sharedPresets");
    return saved ? JSON.parse(saved) : [];
  });
  const [editingPreset, setEditingPreset] = useState(null);
  const [isLinePresetsExpanded, setIsLinePresetsExpanded] = useState(true);
  const [isPropertiesExpanded, setIsPropertiesExpanded] = useState(true);

  // Local state for input values to avoid immediate updates
  const [localInputValues, setLocalInputValues] = useState({
    x: selectedLine?.x ? Math.round(selectedLine.x) : "",
    y: selectedLine?.y ? Math.round(selectedLine.y) : "",
    width: selectedLine?.width ? Math.round(selectedLine.width) : "",
    height: selectedLine?.height ? Math.round(selectedLine.height) : "",
    strokeWidth: selectedLine?.strokeWidth
      ? Math.round(selectedLine.strokeWidth)
      : "",
    startDotRadius: selectedLine?.startDot?.radius
      ? Math.round(selectedLine.startDot.radius)
      : "",
    endDotRadius: selectedLine?.endDot?.radius
      ? Math.round(selectedLine.endDot.radius)
      : "",
    bends: selectedLine?.points
      ? Math.max(0, (selectedLine.points.length - 4) / 2)
      : 0,
    colorHex:
      selectedLine?.stroke || selectedLine?.strokeColor
        ? (selectedLine.stroke || selectedLine.strokeColor)
            .toUpperCase()
            .replace("#", "")
        : "000000",
  });

  // Update local state when selectedLine changes
  useEffect(() => {
    if (selectedLine) {
      setLocalInputValues({
        x: selectedLine.x ? Math.round(selectedLine.x) : "",
        y: selectedLine.y ? Math.round(selectedLine.y) : "",
        width: selectedLine.width ? Math.round(selectedLine.width) : "",
        height: selectedLine.height ? Math.round(selectedLine.height) : "",
        strokeWidth: selectedLine.strokeWidth
          ? Math.round(selectedLine.strokeWidth)
          : "",
        startDotRadius: selectedLine.startDot?.radius
          ? Math.round(selectedLine.startDot.radius)
          : "",
        endDotRadius: selectedLine.endDot?.radius
          ? Math.round(selectedLine.endDot.radius)
          : "",
        bends: selectedLine.points
          ? Math.max(0, (selectedLine.points.length - 4) / 2)
          : 0,
        colorHex:
          selectedLine.stroke || selectedLine.strokeColor
            ? (selectedLine.stroke || selectedLine.strokeColor)
                .toUpperCase()
                .replace("#", "")
            : "000000",
      });
    }
  }, [selectedLine]);

  // Calculate waypoints for bends
  const calculateWaypoints = (currentPoints, bendCount) => {
    if (!currentPoints || currentPoints.length < 4) {
      // Fallback for invalid points
      return [0, 0, 100, 0];
    }

    // Get the actual start and end points of the line
    const startX = currentPoints[0];
    const startY = currentPoints[1];
    const endX = currentPoints[currentPoints.length - 2];
    const endY = currentPoints[currentPoints.length - 1];

    if (bendCount === 0) {
      // No bends - return simple start and end points
      return [startX, startY, endX, endY];
    }

    const newPoints = [startX, startY];

    // Add intermediate waypoints exactly on the straight line
    for (let i = 1; i <= bendCount; i++) {
      const t = i / (bendCount + 1);
      // Place waypoints exactly on the straight line between start and end
      const waypointX = startX + (endX - startX) * t;
      const waypointY = startY + (endY - startY) * t;

      newPoints.push(waypointX, waypointY);
    }

    newPoints.push(endX, endY);
    return newPoints;
  };

  // Helper to validate and parse hex color
  const validateAndParseHexColor = (hexValue) => {
    // Remove # if present and trim whitespace
    let hex = hexValue.trim().replace(/^#/, "").toUpperCase();

    // Validate hex: must be 3 or 6 characters, only 0-9 and A-F
    if (!/^[0-9A-F]{3}$|^[0-9A-F]{6}$/.test(hex)) {
      return null; // Invalid hex
    }

    // Convert 3-digit to 6-digit if needed
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((char) => char + char)
        .join("");
    }

    return `#${hex}`;
  };

  // Helper for number input: allow empty string, clamp values
  const handleNumberInputWithLimits = (field, value, min, max) => {
    let num = parseInt(value, 10);
    if (value === "" || isNaN(num)) {
      updateLineProperties(selectedLine.id, { [field]: min });
      return;
    }
    if (typeof min === "number" && num < min) num = min;
    if (typeof max === "number" && num > max) num = max;

    // Handle bends count changes
    if (field === "bends") {
      const bendCount = Math.max(0, Math.floor(num)); // Ensure non-negative integer
      const currentPoints = selectedLine.points || [0, 0, 0, 0];
      
      // Calculate current bend count from points array
      // Points array format: [x1, y1, x2, y2, ..., xn, yn]
      // Number of bends = (points.length - 4) / 2
      const currentBendCount = Math.max(0, (currentPoints.length - 4) / 2);
      
      // Only recalculate waypoints if the bend count actually changed
      if (bendCount === currentBendCount) {
        return; // Don't recalculate if bend count hasn't changed
      }
      
      const lineX = selectedLine.x || 0;
      const lineY = selectedLine.y || 0;

      // Convert points to relative coordinates if they're absolute
      const isRelative =
        Math.abs(currentPoints[0]) <= 2 && Math.abs(currentPoints[1]) <= 2;
      const relativePoints = isRelative
        ? currentPoints
        : currentPoints.map((coord, i) =>
            i % 2 === 0 ? coord - lineX : coord - lineY
          );

      const newRelativePoints = calculateWaypoints(relativePoints, bendCount);

      // Calculate new dimensions based on the new relative points
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;
      for (let i = 0; i < newRelativePoints.length; i += 2) {
        minX = Math.min(minX, newRelativePoints[i]);
        maxX = Math.max(maxX, newRelativePoints[i]);
        minY = Math.min(minY, newRelativePoints[i + 1]);
        maxY = Math.max(maxY, newRelativePoints[i + 1]);
      }
      const newWidth = maxX - minX;
      const newHeight = maxY - minY;

      updateLineProperties(selectedLine.id, {
        points: newRelativePoints,
        width: newWidth,
        height: newHeight,
        x: lineX,
        y: lineY,
        skipOffset: true, // Prevent position recalculation since we're passing relative points
      });
      return;
    }

    // For width/height, simulate dragging the end point like ArrowEditor does
    if ((field === "width" || field === "height") && selectedLine.points) {
      // Get current points and position
      const currentPoints = selectedLine.points;
      const currentX = selectedLine.x || 0;
      const currentY = selectedLine.y || 0;

      // Convert to absolute points
      const absPoints = currentPoints.map((coord, i) =>
        i % 2 === 0 ? coord + currentX : coord + currentY
      );

      // For height change, move the end point vertically
      if (field === "height") {
        const currentHeight = selectedLine.height || 1;
        const heightDiff = num - currentHeight;

        // Move the end point (last two coordinates) vertically
        absPoints[absPoints.length - 1] += heightDiff;
      }
      // For width change, move the end point horizontally
      else if (field === "width") {
        const currentWidth = selectedLine.width || 1;
        const widthDiff = num - currentWidth;

        // Move the end point (second to last coordinate) horizontally
        absPoints[absPoints.length - 2] += widthDiff;
      }

      // Keep the line in its current position, just update the width/height
      const newWidth = field === "width" ? num : selectedLine.width || 1;
      const newHeight = field === "height" ? num : selectedLine.height || 1;

      // Convert back to relative points (keep current position)
      const newRelativePoints = absPoints.map((coord, i) =>
        i % 2 === 0 ? coord - currentX : coord - currentY
      );

      updateLineProperties(selectedLine.id, {
        [field]: num,
        points: newRelativePoints,
        x: currentX,
        y: currentY,
        width: newWidth,
        height: newHeight,
        skipOffset: true,
      });
    } else {
      updateLineProperties(selectedLine.id, { [field]: num });
    }
  };

  const handleEnterKey = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.target.blur();
    }
  };

  const saveLinePresetsToStorage = (presets) => {
    try {
      localStorage.setItem("sharedPresets", JSON.stringify(presets));
    } catch (error) {
      alert("Failed to save preset. Please check the console for details.");
    }
  };

  const saveCurrentLineStyleAsPreset = () => {
    const usedNames = new Set(linePresets.map((p) => p.name));

    // Find the next unused preset name
    let index = 1;
    let name;
    do {
      name = `Line Preset ${index}`;
      index++;
    } while (usedNames.has(name));

    // Exclude position and other non-style properties when saving preset
    const { x, y, id, name: originalName, ...styleProperties } = selectedLine;

    const preset = {
      type: "line", // Identify this as a line preset
      // Store only style properties, not position or geometry
      ...styleProperties,
      id: Date.now() + Math.random(), // More unique ID generation
      name,
    };

    const updatedPresets = [...linePresets, preset];
    setLinePresets(updatedPresets);
    saveLinePresetsToStorage(updatedPresets);

    // Immediately show the edit box for the newly created preset
    setEditingPreset(preset);
  };

  const applyLinePreset = (preset) => {
    // Filter out arrow-specific properties when applying arrow presets to lines
    const isArrowPreset = preset.type === "arrow";

    if (isArrowPreset) {
      // For arrow presets applied to lines, only copy visual properties
      // and ignore geometric properties like points, bends, position, etc.
      const {
        points,
        bends,
        angle,
        rotation,
        width,
        height,
        x,
        y,
        id,
        name,
        type,
        ...visualProperties
      } = preset;

      updateLineProperties(selectedLine.id, {
        ...visualProperties,
        // Preserve the current line's ID
        id: selectedLine.id,
        // Mark as line preset application
        type: "Line",
        name: selectedLine.name,
      });
    } else {
      // Apply visual properties and geometry pattern, but preserve position and current size
      const {
        startDot,
        endDot,
        points: presetPoints,
        width: presetWidth,
        height: presetHeight,
        x,
        y,
        id,
        name,
        type,
        ...visualProperties
      } = preset;

      // Get current line's position (preserve only the position)
      const currentX = selectedLine.x || 0;
      const currentY = selectedLine.y || 0;

      if (
        !presetPoints ||
        presetPoints.length < 4 ||
        presetWidth === undefined ||
        presetHeight === undefined
      ) {
        // Fallback: just apply visual properties
        updateLineProperties(selectedLine.id, {
          ...visualProperties,
          id: selectedLine.id,
          type: "Line",
          name: selectedLine.name,
        });
        return;
      }

      // Normalize preset points to start from (0,0)
      // Find the minimum x and y in preset points
      let minX = Infinity;
      let minY = Infinity;
      for (let i = 0; i < presetPoints.length; i += 2) {
        minX = Math.min(minX, presetPoints[i]);
        minY = Math.min(minY, presetPoints[i + 1]);
      }

      // Subtract the offset so points start from (0,0) or close to it
      const normalizedPoints = [];
      for (let i = 0; i < presetPoints.length; i += 2) {
        normalizedPoints.push(
          presetPoints[i] - minX,
          presetPoints[i + 1] - minY
        );
      }

      // Apply the preset's exact geometry with normalized points
      // Only the position (x, y) is taken from the current line
      updateLineProperties(selectedLine.id, {
        ...visualProperties,
        points: normalizedPoints, // Use normalized points
        width: presetWidth, // Use preset's width
        height: presetHeight, // Use preset's height
        x: currentX, // Keep current position
        y: currentY, // Keep current position
        skipOffset: true,
        // Preserve the current line's ID
        id: selectedLine.id,
        // Ensure type is passed to identify this as a preset application
        type: "Line",
        name: selectedLine.name,
      });
    }
  };

  const handlePropertyChange = (field, value) => {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue)) return;
    updateLineProperties(selectedLine.id, { [field]: numValue });
  };

  return (
    <div>
      {/* Position and Dimensions */}
      <div
        style={{
          background: "#f5f5f5",
          borderRadius: "8px",
          padding: "10px 12px",
          marginBottom: "12px",
        }}
      >
        {/* Row 1: Position */}
        <div
          style={{
            display: "flex",
            gap: "16px",
            alignItems: "center",
            marginBottom: "8px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <label style={{ whiteSpace: "nowrap" }}>XPos</label>
            <input
              type="number"
              value={getDisplayValue(localInputValues.x)}
              min={MIN_X}
              max={MAX_X}
              onKeyDown={handleEnterKey}
              onChange={(e) => {
                const { value } = e.target;
                const inputType = e.nativeEvent?.inputType;
                setLocalInputValues((prev) => ({
                  ...prev,
                  x: value,
                }));
                if (value === "" || shouldDeferInputUpdate(inputType)) {
                  return;
                }
                const clamped = clampNumericValue(value, MIN_X, MAX_X);
                if (clamped === null) {
                  return;
                }
                handleNumberInputWithLimits("x", String(clamped), MIN_X, MAX_X);
                setLocalInputValues((prev) => ({
                  ...prev,
                  x: clamped,
                }));
              }}
              onBlur={(e) => {
                const val = e.target.value;
                if (val === "") {
                  handleNumberInputWithLimits("x", String(MIN_X), MIN_X, MAX_X);
                } else {
                  handleNumberInputWithLimits(
                    "x",
                    String(parseInt(val, 10)),
                    MIN_X,
                    MAX_X
                  );
                }
              }}
              style={{
                width: "60px",
                padding: "6px 8px",
                marginLeft: "7px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <label style={{ whiteSpace: "nowrap" }}>YPos</label>
            <input
              type="number"
              value={getDisplayValue(localInputValues.y)}
              min={MIN_Y}
              max={MAX_Y}
              onKeyDown={handleEnterKey}
              onChange={(e) => {
                const { value } = e.target;
                const inputType = e.nativeEvent?.inputType;
                setLocalInputValues((prev) => ({
                  ...prev,
                  y: value,
                }));
                if (value === "" || shouldDeferInputUpdate(inputType)) {
                  return;
                }
                const clamped = clampNumericValue(value, MIN_Y, MAX_Y);
                if (clamped === null) {
                  return;
                }
                handleNumberInputWithLimits("y", String(clamped), MIN_Y, MAX_Y);
                setLocalInputValues((prev) => ({
                  ...prev,
                  y: clamped,
                }));
              }}
              onBlur={(e) => {
                const val = e.target.value;
                if (val === "") {
                  handleNumberInputWithLimits("y", String(MIN_Y), MIN_Y, MAX_Y);
                } else {
                  handleNumberInputWithLimits(
                    "y",
                    String(parseInt(val, 10)),
                    MIN_Y,
                    MAX_Y
                  );
                }
              }}
              style={{
                width: "60px",
                padding: "6px 8px",
                marginLeft: "10px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            />
          </div>
        </div>

        {/* Row 2: Dimensions */}
        <div
          style={{
            display: "flex",
            gap: "16px",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <label style={{ whiteSpace: "nowrap" }}>Width</label>
            <input
              type="number"
              value={localInputValues.width}
              min={MIN_WIDTH}
              max={MAX_WIDTH}
              onKeyDown={handleEnterKey}
              onChange={(e) => {
                const { value } = e.target;
                const inputType = e.nativeEvent?.inputType;
                setLocalInputValues((prev) => ({
                  ...prev,
                  width: value,
                }));
                if (value === "" || shouldDeferInputUpdate(inputType)) {
                  return;
                }
                const clamped = clampNumericValue(value, MIN_WIDTH, MAX_WIDTH);
                if (clamped === null) {
                  return;
                }
                handleNumberInputWithLimits(
                  "width",
                  String(clamped),
                  MIN_WIDTH,
                  MAX_WIDTH
                );
                setLocalInputValues((prev) => ({
                  ...prev,
                  width: clamped,
                }));
              }}
              onBlur={(e) => {
                const val = e.target.value;
                if (val === "") {
                  handleNumberInputWithLimits(
                    "width",
                    String(MIN_WIDTH),
                    MIN_WIDTH,
                    MAX_WIDTH
                  );
                } else {
                  handleNumberInputWithLimits(
                    "width",
                    String(parseInt(val, 10)),
                    MIN_WIDTH,
                    MAX_WIDTH
                  );
                }
              }}
              style={{
                width: "60px",
                padding: "6px 8px",
                marginLeft: "3px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <label style={{ whiteSpace: "nowrap" }}>Height</label>
            <input
              type="number"
              value={localInputValues.height}
              min={MIN_HEIGHT}
              max={MAX_HEIGHT}
              onKeyDown={handleEnterKey}
              onChange={(e) => {
                const { value } = e.target;
                const inputType = e.nativeEvent?.inputType;
                setLocalInputValues((prev) => ({
                  ...prev,
                  height: value,
                }));
                if (value === "" || shouldDeferInputUpdate(inputType)) {
                  return;
                }
                const clamped = clampNumericValue(
                  value,
                  MIN_HEIGHT,
                  MAX_HEIGHT
                );
                if (clamped === null) {
                  return;
                }
                handleNumberInputWithLimits(
                  "height",
                  String(clamped),
                  MIN_HEIGHT,
                  MAX_HEIGHT
                );
                setLocalInputValues((prev) => ({
                  ...prev,
                  height: clamped,
                }));
              }}
              onBlur={(e) => {
                const val = e.target.value;
                if (val === "") {
                  handleNumberInputWithLimits(
                    "height",
                    String(MIN_HEIGHT),
                    MIN_HEIGHT,
                    MAX_HEIGHT
                  );
                } else {
                  handleNumberInputWithLimits(
                    "height",
                    String(parseInt(val, 10)),
                    MIN_HEIGHT,
                    MAX_HEIGHT
                  );
                }
              }}
              style={{
                width: "60px",
                padding: "6px 8px",
                marginLeft: "3px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            />
          </div>
        </div>
      </div>

      {/* Properties Section */}
      <div
        style={{
          background: "#f7f7f7",
          borderRadius: "8px",
          padding: "16px",
          marginBottom: "12px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
            cursor: "pointer",
          }}
          onClick={() => setIsPropertiesExpanded(!isPropertiesExpanded)}
        >
          <h4 style={{ margin: "0", fontWeight: "normal", fontSize: "14px" }}>
            Properties
          </h4>
          <svg
            width="12"
            height="7"
            viewBox="0 0 14 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              transform: isPropertiesExpanded
                ? "rotate(0deg)"
                : "rotate(180deg)",
              transition: "transform 0.2s",
            }}
          >
            <path
              d="M1.61906 7.75961L0.734375 6.87492L7.36906 0.240234L14.0038 6.87492L13.1191 7.75961L7.36906 2.0168L1.61906 7.75961Z"
              fill="#6C757D"
            />
          </svg>
        </div>

        {isPropertiesExpanded && (
          <>
            {/* Stroke Size */}
            <div
              style={{
                marginBottom: "12px",
                display: "flex",
                gap: "16px",
                alignItems: "center",
              }}
            >
              <label style={{ whiteSpace: "nowrap", fontSize: "13px" }}>
                Stroke Size
              </label>
              <input
                type="number"
                min={MIN_STROKE_WIDTH}
                max={MAX_STROKE_WIDTH}
                value={localInputValues.strokeWidth}
                onKeyDown={handleEnterKey}
                onChange={(e) => {
                  const { value } = e.target;
                  const inputType = e.nativeEvent?.inputType;
                  setLocalInputValues((prev) => ({
                    ...prev,
                    strokeWidth: value,
                  }));
                  if (value === "" || shouldDeferInputUpdate(inputType)) {
                    return;
                  }
                  const clamped = clampNumericValue(
                    value,
                    MIN_STROKE_WIDTH,
                    MAX_STROKE_WIDTH
                  );
                  if (clamped === null) {
                    return;
                  }
                  handleNumberInputWithLimits(
                    "strokeWidth",
                    String(clamped),
                    MIN_STROKE_WIDTH,
                    MAX_STROKE_WIDTH
                  );
                  setLocalInputValues((prev) => ({
                    ...prev,
                    strokeWidth: clamped,
                  }));
                }}
                onBlur={(e) => {
                  const val = e.target.value;
                  if (val === "") {
                    handleNumberInputWithLimits(
                      "strokeWidth",
                      String(MIN_STROKE_WIDTH),
                      MIN_STROKE_WIDTH,
                      MAX_STROKE_WIDTH
                    );
                  } else {
                    handleNumberInputWithLimits(
                      "strokeWidth",
                      String(parseInt(val, 10)),
                      MIN_STROKE_WIDTH,
                      MAX_STROKE_WIDTH
                    );
                  }
                }}
                style={{
                  width: "100%",
                  padding: "4px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                }}
              />
            </div>

            {/* Bends */}
            <div
              style={{
                marginBottom: "12px",
                display: "flex",
                gap: "16px",
                alignItems: "center",
              }}
            >
              <label style={{ whiteSpace: "nowrap", fontSize: "13px" }}>
                Bends
              </label>
              <input
                type="number"
                min="0"
                value={localInputValues.bends}
                onKeyDown={handleEnterKey}
                onChange={(e) => {
                  const { value } = e.target;
                  const inputType = e.nativeEvent?.inputType;
                  setLocalInputValues((prev) => ({
                    ...prev,
                    bends: value,
                  }));
                  if (value === "" || shouldDeferInputUpdate(inputType)) {
                    return;
                  }
                  const clamped = clampNumericValue(value, 0, undefined);
                  if (clamped === null) {
                    return;
                  }
                  handleNumberInputWithLimits("bends", String(clamped), 0, undefined);
                  setLocalInputValues((prev) => ({
                    ...prev,
                    bends: clamped,
                  }));
                }}
                onBlur={(e) => {
                  const val = e.target.value;
                  if (val === "") {
                    handleNumberInputWithLimits(
                      "bends",
                      String(0),
                      0,
                      undefined // No max limit
                    );
                  } else {
                    handleNumberInputWithLimits(
                      "bends",
                      String(parseInt(val, 10)),
                      0,
                      undefined // No max limit
                    );
                  }
                }}
                style={{
                  width: "100%",
                  padding: "4px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                }}
              />
            </div>

            {/* Color */}
            <div
              style={{
                marginBottom: "12px",
                display: "flex",
                gap: "16px",
                alignItems: "center",
              }}
            >
              <label style={{ whiteSpace: "nowrap", fontSize: "13px" }}>
                Color
              </label>
              <div
                style={{ display: "flex", gap: "8px", alignItems: "center" }}
              >
                <button
                  onClick={() => {
                    const newColor = "#dc3545";
                    updateLineProperties(selectedLine.id, {
                      strokeColor: newColor,
                      stroke: newColor,
                    });
                    setLocalInputValues((prev) => ({
                      ...prev,
                      colorHex: newColor.toUpperCase().replace("#", ""),
                    }));
                  }}
                  style={{
                    width: "24px",
                    height: "24px",
                    backgroundColor: "#dc3545",
                    border: "1px solid #ccc",
                    borderRadius: "2px",
                    cursor: "pointer",
                  }}
                />
                <button
                  onClick={() => {
                    const newColor = "#fd7e14";
                    updateLineProperties(selectedLine.id, {
                      strokeColor: newColor,
                      stroke: newColor,
                    });
                    setLocalInputValues((prev) => ({
                      ...prev,
                      colorHex: newColor.toUpperCase().replace("#", ""),
                    }));
                  }}
                  style={{
                    width: "24px",
                    height: "24px",
                    backgroundColor: "#fd7e14",
                    border: "1px solid #ccc",
                    borderRadius: "2px",
                    cursor: "pointer",
                  }}
                />
                <input
                  type="color"
                  value={strokeColor}
                  onChange={(e) => {
                    const newColor = e.target.value;
                    updateLineProperties(selectedLine.id, {
                      strokeColor: newColor,
                      stroke: newColor,
                    });
                    // Update local hex input to stay in sync
                    setLocalInputValues((prev) => ({
                      ...prev,
                      colorHex: newColor.toUpperCase().replace("#", ""),
                    }));
                  }}
                  style={{
                    width: "40px",
                    height: "24px",
                    border: "1px solid #ccc",
                    borderRadius: "2px",
                    cursor: "pointer",
                    padding: 0,
                  }}
                />
                <input
                  type="text"
                  value={`${localInputValues.colorHex}`}
                  maxLength={10}
                  onKeyDown={handleEnterKey}
                  onChange={(e) => {
                    // Strip "Hex " prefix if present, then process hex value
                    let inputValue = e.target.value
                      .replace(/^Hex\s*/i, "")
                      .toUpperCase()
                      .replace("#", "");
                    // Limit to max 6 hex characters
                    if (inputValue.length > 6) {
                      inputValue = inputValue.slice(0, 6);
                    }
                    setLocalInputValues((prev) => ({
                      ...prev,
                      colorHex: inputValue,
                    }));
                  }}
                  onBlur={(e) => {
                    // Strip "Hex " prefix before validating
                    const hexValue = e.target.value.replace(/^Hex\s*/i, "");
                    const validColor = validateAndParseHexColor(hexValue);

                    if (validColor) {
                      // Valid hex code - update the stroke color
                      updateLineProperties(selectedLine.id, {
                        strokeColor: validColor,
                        stroke: validColor,
                      });
                    } else {
                      // Invalid hex code - revert to current color
                      const currentHex = strokeColor
                        .toUpperCase()
                        .replace("#", "");
                      setLocalInputValues((prev) => ({
                        ...prev,
                        colorHex: currentHex,
                      }));
                    }
                  }}
                  placeholder="Hex 000000"
                  style={{
                    flex: 1,
                    padding: "4px 8px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    backgroundColor: "white",
                    fontSize: "12px",
                  }}
                />
              </div>
            </div>

            {/* Arrow Head Options */}
            <div
              style={{
                marginBottom: "12px",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              <label style={{ fontSize: "13px", fontWeight: "500" }}>
                Arrow Heads
              </label>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "13px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedLine.pointerAtBeginning || false}
                    onChange={(e) => {
                      updateLineProperties(selectedLine.id, {
                        pointerAtBeginning: e.target.checked,
                      });
                    }}
                    style={{
                      width: "16px",
                      height: "16px",
                      cursor: "pointer",
                    }}
                  />
                  <span>Arrow at Beginning</span>
                </label>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "13px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedLine.pointerAtEnd || false}
                    onChange={(e) => {
                      updateLineProperties(selectedLine.id, {
                        pointerAtEnd: e.target.checked,
                      });
                    }}
                    style={{
                      width: "16px",
                      height: "16px",
                      cursor: "pointer",
                    }}
                  />
                  <span>Arrow at End</span>
                </label>
              </div>
            </div>

            <button
              onClick={saveCurrentLineStyleAsPreset}
              style={{
                background: "#f7f7f7",
                border: "none",
                width: "100%",
                color: "#007bff",
                fontSize: "13px",
              }}
            >
              <span
                style={{
                  fontSize: "16px",
                  marginRight: "8px",
                }}
              >
                +
              </span>
              Add These Properties as a Style
            </button>
          </>
        )}
      </div>

      {/* Add to Preset Button */}
      <div
        style={{
          marginBottom: "12px",
          display: "flex",
          alignItems: "center",
        }}
      ></div>

      {/* Preset Controls */}
      <div
        style={{
          background: "#f7f7f7",
          borderRadius: "5px",
          marginTop: "-10px",
          padding: "1rem",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
          }}
          onClick={() => setIsLinePresetsExpanded(!isLinePresetsExpanded)}
        >
          <h4 style={{ margin: "0", fontWeight: "normal" }}>Styles</h4>
          <svg
            width="20"
            height="12"
            viewBox="0 0 14 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              transform: isLinePresetsExpanded
                ? "rotate(0deg)"
                : "rotate(180deg)",
              transition: "transform 0.2s",
            }}
          >
            <path
              d="M1.61906 7.75961L0.734375 6.87492L7.36906 0.240234L14.0038 6.87492L13.1191 7.75961L7.36906 2.0168L1.61906 7.75961Z"
              fill="#6C757D"
            />
          </svg>
        </div>

        {isLinePresetsExpanded && (
          <>
            {/* List Presets */}
            {linePresets.map((preset) => (
              <div
                key={preset.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "10px 0",
                  borderBottom: "1px solid #eee",
                }}
              >
                <button
                  onClick={() => applyLinePreset(preset)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "left",
                    flexGrow: 1,
                    padding: 0,
                    fontSize: "1rem",
                  }}
                >
                  {preset.name}
                </button>
                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    onClick={() => setEditingPreset(preset)}
                    title="Edit"
                    style={{
                      backgroundColor: "#007bff",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      padding: "2px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "22px",
                      height: "22px",
                    }}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clip-path="url(#clip0_15909_16836)">
                        <path
                          d="M11.6263 1.45481C11.6964 1.5251 11.7357 1.6203 11.7357 1.71956C11.7357 1.81881 11.6964 1.91401 11.6263 1.98431L10.844 2.76731L9.34404 1.26731L10.1263 0.484307C10.1966 0.414005 10.292 0.374512 10.3914 0.374512C10.4908 0.374512 10.5862 0.414005 10.6565 0.484307L11.6263 1.45406V1.45481ZM10.3138 3.29681L8.81379 1.79681L3.70404 6.90731C3.66276 6.94858 3.63168 6.99891 3.61329 7.05431L3.00954 8.86481C2.99859 8.89781 2.99703 8.93321 3.00505 8.96705C3.01306 9.00089 3.03033 9.03184 3.05492 9.05643C3.07951 9.08102 3.11045 9.09828 3.14429 9.1063C3.17813 9.11431 3.21353 9.11276 3.24654 9.10181L5.05704 8.49806C5.11236 8.47988 5.16269 8.44906 5.20404 8.40806L10.3138 3.29681Z"
                          fill="white"
                        />
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M0.75 10.125C0.75 10.4234 0.868526 10.7095 1.0795 10.9205C1.29048 11.1315 1.57663 11.25 1.875 11.25H10.125C10.4234 11.25 10.7095 11.1315 10.9205 10.9205C11.1315 10.7095 11.25 10.4234 11.25 10.125V5.625C11.25 5.52554 11.2105 5.43016 11.1402 5.35984C11.0698 5.28951 10.9745 5.25 10.875 5.25C10.7755 5.25 10.6802 5.28951 10.6098 5.35984C10.5395 5.43016 10.5 5.52554 10.5 5.625V10.125C10.5 10.2245 10.4605 10.3198 10.3902 10.3902C10.3198 10.4605 10.2245 10.5 10.125 10.5H1.875C1.77554 10.5 1.68016 10.4605 1.60984 10.3902C1.53951 10.3198 1.5 10.2245 1.5 10.125V1.875C1.5 1.77554 1.53951 1.68016 1.60984 1.60984C1.68016 1.53951 1.77554 1.5 1.875 1.5H6.75C6.84946 1.5 6.94484 1.46049 7.01517 1.39017C7.08549 1.31984 7.125 1.22446 7.125 1.125C7.125 1.02554 7.08549 0.930161 7.01517 0.859835C6.94484 0.789509 6.84946 0.75 6.75 0.75H1.875C1.57663 0.75 1.29048 0.868526 1.0795 1.0795C0.868526 1.29048 0.75 1.57663 0.75 1.875V10.125Z"
                          fill="white"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_15909_16836">
                          <rect width="12" height="12" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </button>
                  <button
                    onClick={() => {
                      const filtered = linePresets.filter(
                        (p) => p.id !== preset.id
                      );
                      setLinePresets(filtered);
                      saveLinePresetsToStorage(filtered);
                      // Clear editing state if the deleted preset is being edited
                      if (editingPreset && editingPreset.id === preset.id) {
                        setEditingPreset(null);
                      }
                    }}
                    title="Delete"
                    style={{
                      backgroundColor: "#dc3545",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      padding: "2px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "22px",
                      height: "22px",
                    }}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect width="18" height="18" rx="4" fill="#DC3545" />
                      <path
                        d="M5.875 14.5C5.53125 14.5 5.23698 14.3803 4.99219 14.141C4.7474 13.9016 4.625 13.6139 4.625 13.2778V5.33333H4V4.11111H7.125V3.5H10.875V4.11111H14V5.33333H13.375V13.2778C13.375 13.6139 13.2526 13.9016 13.0078 14.141C12.763 14.3803 12.4688 14.5 12.125 14.5H5.875ZM7.125 12.0556H8.375V6.55556H7.125V12.0556ZM9.625 12.0556H10.875V6.55556H9.625V12.0556Z"
                        fill="white"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ))}

            {editingPreset && (
              <div
                style={{
                  marginTop: "16px",
                  padding: "12px",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                }}
              >
                <h4>Edit Preset: {editingPreset.name}</h4>
                <input
                  type="text"
                  value={editingPreset.name}
                  onChange={(e) =>
                    setEditingPreset({
                      ...editingPreset,
                      name: e.target.value,
                    })
                  }
                  placeholder="Preset name"
                  style={{
                    width: "100%",
                    padding: "6px",
                    border: "1px solid #ccc",
                    marginBottom: "12px",
                  }}
                />

                <button
                  onClick={() => {
                    const trimmedName = editingPreset.name.trim();

                    // Check for duplicate name (excluding the current editing one)
                    const isDuplicate = linePresets.some(
                      (p) =>
                        p.id !== editingPreset.id &&
                        p.name.trim().toLowerCase() ===
                          trimmedName.toLowerCase()
                    );

                    if (isDuplicate) {
                      alert(
                        "A preset with this name already exists. Please choose a different name."
                      );
                      return;
                    }

                    const updated = linePresets.map((p) =>
                      p.id === editingPreset.id
                        ? { ...editingPreset, name: trimmedName }
                        : p
                    );

                    setLinePresets(updated);
                    saveLinePresetsToStorage(updated);
                    setEditingPreset(null);
                  }}
                  style={{
                    backgroundColor: "#2563eb",
                    color: "white",
                    padding: "6px 12px",
                    borderRadius: "4px",
                    border: "none",
                    cursor: "pointer",
                    marginRight: "8px",
                  }}
                >
                  Save Changes
                </button>

                <button
                  onClick={() => setEditingPreset(null)}
                  style={{
                    backgroundColor: "#e5e7eb",
                    color: "#111827",
                    padding: "6px 12px",
                    borderRadius: "4px",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </>
        )}
      </div>
      {/* Delete Button */}
      <div style={{ marginTop: "16px" }}>
        <button
          onClick={onDelete}
          style={{
            width: "100%",
            height: "40px",
            backgroundColor: "white",
            borderRadius: "5px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            border: "2px solid red",
            color: "red",
            marginTop: "10px",
          }}
        >
          Delete Line
        </button>
      </div>
    </div>
  );
}

LineEditor.propTypes = {
  selectedLine: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    x: PropTypes.number,
    y: PropTypes.number,
    length: PropTypes.number,
    angle: PropTypes.number,
    strokeColor: PropTypes.string,
    strokeWidth: PropTypes.number,
    isDashed: PropTypes.bool,
    startDot: PropTypes.object,
    endDot: PropTypes.object,
    points: PropTypes.arrayOf(PropTypes.number),
  }).isRequired,
  updateLineProperties: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

LineEditor.defaultProps = {
  selectedLine: null,
};

export default LineEditor;
